import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  Grid,
  CircularProgress,
  Autocomplete,
  Chip,
  Breadcrumbs,
  Link,
  Stack,
  Alert,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useNavigate, useParams, Link as RouterLink } from "react-router-dom";
import { getUser, updateUser } from "../../api/users";
import { getRolesList } from "../../api/roles";
import useAuth from "../../hooks/useAuth";
import { getTenantsList } from "../../api/tenant";
import { Add } from "@mui/icons-material";
import RoleForm from "../roles/RoleForm";
import { useNotify } from "../../utils/useNotify";
import { APP_HOME_PATH } from "../../config/routes";
import { formatAxiosError } from "../../utils/apiError";

function mapUserToInitialValues(user, roleObjects, isSuperAdmin) {
  return {
    firstname: user.firstname ?? "",
    lastname: user.lastname ?? "",
    email: user.email ?? "",
    username: user.username ?? "",
    is_active: Boolean(user.is_active),
    tenant_id: isSuperAdmin ? user.tenant_id ?? null : user.tenant_id,
    roles: roleObjects.filter(Boolean),
  };
}

export default function UserForm() {
  const { id } = useParams();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const notify = useNotify();

  const [pageLoading, setPageLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [boot, setBoot] = useState(null);
  const [roleOptions, setRoleOptions] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [createRoleDialog, setCreateRoleDialog] = useState(false);

  const isSuperAdmin = Array.isArray(currentUser?.roles)
    ? currentUser.roles.some((r) =>
        typeof r === "string" ? r === "super_admin" : r?.name === "super_admin"
      )
    : false;

  const canManageRoles = Boolean(
    currentUser?.permissions?.includes("manage_roles")
  );

  const loadRolesForTenant = useCallback(async (tenantId) => {
    if (tenantId == null) {
      setRoleOptions([]);
      return [];
    }
    const list = await getRolesList({
      skip: 0,
      limit: 200,
      tenant_id: tenantId,
    });
    const arr = Array.isArray(list) ? list : [];
    setRoleOptions(arr);
    return arr;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setPageLoading(true);
      setLoadError(null);
      setBoot(null);
      try {
        const u = await getUser(id);
        if (cancelled) return;

        let tenantList = [];
        if (isSuperAdmin) {
          const raw = await getTenantsList({ for_filter: true });
          tenantList = Array.isArray(raw) ? raw : [];
        }
        if (cancelled) return;
        setTenants(tenantList);

        const ro = await loadRolesForTenant(u.tenant_id);
        if (cancelled) return;

        const mappedRoles = (u.roles || []).map((r) => {
          const full = ro.find((x) => x.id === r.id);
          return full || { id: r.id, name: r.name, tenant_id: u.tenant_id };
        });

        setBoot({
          user: u,
          initialValues: mapUserToInitialValues(u, mappedRoles, isSuperAdmin),
        });
      } catch (e) {
        if (!cancelled) {
          setLoadError(formatAxiosError(e, "Could not load user"));
        }
      } finally {
        if (!cancelled) setPageLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isSuperAdmin, loadRolesForTenant]);

  const validationSchema = useMemo(() => {
    const base = {
      firstname: yup.string().required("First name is required"),
      lastname: yup.string().required("Last name is required"),
      email: yup.string().email("Valid email required").required(),
      username: yup.string().required("Username is required"),
      is_active: yup.boolean(),
    };
    if (canManageRoles) {
      base.roles = yup
        .array()
        .test(
          "roles-when-tenant",
          "Select at least one role for this organization",
          function rolesTest(val) {
            const tid = this.parent.tenant_id;
            if (tid == null) return true;
            return Array.isArray(val) && val.length >= 1;
          }
        );
    }
    if (isSuperAdmin) {
      base.tenant_id = yup.number().nullable();
    }
    return yup.object(base);
  }, [canManageRoles, isSuperAdmin]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues:
      boot?.initialValues ?? {
        firstname: "",
        lastname: "",
        email: "",
        username: "",
        is_active: true,
        tenant_id: null,
        roles: [],
      },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const body = {
          firstname: values.firstname.trim(),
          lastname: values.lastname.trim(),
          email: values.email.trim(),
          username: values.username.trim(),
          is_active: values.is_active,
        };
        if (canManageRoles) {
          body.roles = values.roles.map((r) => r.id);
        }
        if (isSuperAdmin) {
          body.tenant_id = values.tenant_id;
        }
        await updateUser(id, body);
        notify.success("User updated.");
        navigate("/admin/users");
      } catch (e) {
        notify.error(formatAxiosError(e, "Could not update user"));
      }
    },
  });

  const handleTenantChange = async (_, newTenant) => {
    const tid = newTenant?.id ?? null;
    formik.setFieldValue("tenant_id", tid);
    await loadRolesForTenant(tid);
    formik.setFieldValue("roles", []);
    if (tid != null) {
      notify.info("Select one or more roles for this organization.");
    }
  };

  if (pageLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ p: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link component={RouterLink} underline="hover" color="inherit" to={APP_HOME_PATH}>
            Dashboard
          </Link>
          <Link
            component={RouterLink}
            underline="hover"
            color="inherit"
            to="/admin/users"
          >
            Users
          </Link>
          <Typography color="text.primary">Edit</Typography>
        </Breadcrumbs>
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
        <Button variant="contained" component={RouterLink} to="/admin/users">
          Back to users
        </Button>
      </Box>
    );
  }

  if (!boot) {
    return null;
  }

  return (
    <Box sx={{ p: 3, maxWidth: 720, mx: "auto" }}>
      <Breadcrumbs sx={{ mb: 2 }} aria-label="breadcrumb">
        <Link component={RouterLink} underline="hover" color="inherit" to={APP_HOME_PATH}>
          Dashboard
        </Link>
        <Link
          component={RouterLink}
          underline="hover"
          color="inherit"
          to="/admin/users"
        >
          Users
        </Link>
        <Typography color="text.primary">Edit user</Typography>
      </Breadcrumbs>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Button
          variant="outlined"
          component={RouterLink}
          to="/admin/users"
          size="small"
        >
          Back to list
        </Button>
        <Button
          variant="text"
          component={RouterLink}
          to={`/admin/users/${id}`}
          size="small"
        >
          View profile
        </Button>
      </Stack>

      <Typography variant="h4" gutterBottom>
        Edit user
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        New accounts should be invited from{" "}
        <Link component={RouterLink} to="/admin/invites">
          Invitations
        </Link>
        .
      </Typography>

      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              name="firstname"
              label="First name"
              value={formik.values.firstname}
              onChange={formik.handleChange}
              error={
                formik.touched.firstname && Boolean(formik.errors.firstname)
              }
              helperText={formik.touched.firstname && formik.errors.firstname}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              name="lastname"
              label="Last name"
              value={formik.values.lastname}
              onChange={formik.handleChange}
              error={formik.touched.lastname && Boolean(formik.errors.lastname)}
              helperText={formik.touched.lastname && formik.errors.lastname}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              name="email"
              label="Email"
              type="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              name="username"
              label="Username"
              value={formik.values.username}
              onChange={formik.handleChange}
              error={formik.touched.username && Boolean(formik.errors.username)}
              helperText={formik.touched.username && formik.errors.username}
            />
          </Grid>

          {isSuperAdmin && (
            <Grid item xs={12}>
              <Autocomplete
                options={tenants}
                getOptionLabel={(o) => o?.name ?? ""}
                value={tenants.find((t) => t.id === formik.values.tenant_id) ?? null}
                onChange={handleTenantChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Organization"
                    helperText="Users without an organization cannot hold tenant roles."
                  />
                )}
                isOptionEqualToValue={(a, b) => a?.id === b?.id}
              />
            </Grid>
          )}

          {canManageRoles && (
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={roleOptions}
                getOptionLabel={(o) => o?.name ?? ""}
                value={formik.values.roles}
                onChange={(_, v) => formik.setFieldValue("roles", v)}
                disabled={formik.values.tenant_id == null}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Roles"
                    error={formik.touched.roles && Boolean(formik.errors.roles)}
                    helperText={
                      (formik.touched.roles && formik.errors.roles) ||
                      (formik.values.tenant_id == null
                        ? "Assign an organization to enable roles."
                        : "")
                    }
                  />
                )}
                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option.name}
                      {...getTagProps({ index })}
                      key={option.id}
                    />
                  ))
                }
              />
              {!isSuperAdmin && (
                <Button
                  size="small"
                  startIcon={<Add />}
                  onClick={() => setCreateRoleDialog(true)}
                  sx={{ mt: 1 }}
                >
                  Create role
                </Button>
              )}
            </Grid>
          )}

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  name="is_active"
                  checked={formik.values.is_active}
                  onChange={formik.handleChange}
                />
              }
              label="Active"
            />
          </Grid>

          <Grid item xs={12}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Button
                color="primary"
                variant="contained"
                type="submit"
                fullWidth
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                Save changes
              </Button>
              <Button
                variant="outlined"
                component={RouterLink}
                to="/admin/users"
                fullWidth
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                Cancel
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </form>

      <Dialog
        open={createRoleDialog}
        onClose={() => setCreateRoleDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create role</DialogTitle>
        <DialogContent>
          <RoleForm
            onSuccess={(newRole) => {
              setRoleOptions((prev) => [...prev, newRole]);
              setCreateRoleDialog(false);
              notify.success("Role created.");
            }}
            onCancel={() => setCreateRoleDialog(false)}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}
